/**
 * Service Worker 経由でリリース直後のページ自動リロードを有効化する。
 *
 * - `controllerchange`: 新 SW が既存タブを掌握した瞬間に `window.location.reload()`
 * - ユーザ操作 (`pointerdown` / `keydown`) / `focus` / `visibilitychange` / `online`:
 *   能動的に触っている・戻ってきたタイミングで SW の更新チェックを即時に叩く。
 *   操作中のユーザには (固定間隔を待たず) 最速で新バージョンが行き渡る。
 * - 定期ポーリング (`POLL_INTERVAL_MS`): 開きっぱなし & 表示中だが操作の無いタブ向けの安全網。
 *   表示中のときだけ叩く (hidden タブはブラウザが setInterval を throttle する上、
 *   visibilitychange で復帰時に拾えるため)。`reg.update()` は sw.js (数KB) の条件付き再検証のみで、
 *   変更が無ければ新 SW も入らず負荷・端末影響は軽微。
 *
 * すべての契機は `MIN_UPDATE_GAP_MS` のスロットルを通す。連続操作 (タップ/タイピング) や
 * focus+visibilitychange の同時発火でも更新リクエストは一定間隔に1本までに抑えられ、
 * NW が厳しい端末 (iPad 等) や CloudFront/S3 のコストへの影響を上限管理する (PRODUCT_DEV-550)。
 *
 * 前提: vite-plugin-pwa が `registerType: "autoUpdate"` で SW を登録し、
 * workbox で `skipWaiting: true` + `clientsClaim: true` が有効になっていること。
 * これらが揃って初めて、リリース後にユーザがブラウザを開きっぱなしでも最新版が自動反映される。
 *
 * 各プロダクトの main.tsx 冒頭で 1 回呼び出す。SSR 環境では no-op。
 */
// 操作・復帰の無い開きっぱなしタブ向けの定期ポーリング間隔 (安全網)。
const POLL_INTERVAL_MS = 30_000;
// 全契機共通の最小発火間隔 (スロットル)。操作中は即時性を保ちつつ更新リクエストの上限を管理する。
const MIN_UPDATE_GAP_MS = 10_000;

export function registerServiceWorkerAutoReload(): void {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return;
  }

  // 新 SW が既存タブを掌握した瞬間に即リロード
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    window.location.reload();
  });

  // 直近の更新チェック時刻。MIN_UPDATE_GAP_MS 以内の再呼び出しはスキップ (leading-edge throttle)。
  let lastUpdateAt = 0;
  const triggerUpdate = (): void => {
    if (document.visibilityState !== "visible") return;
    const now = Date.now();
    if (now - lastUpdateAt < MIN_UPDATE_GAP_MS) return;
    lastUpdateAt = now;
    void navigator.serviceWorker.getRegistration().then((reg) => {
      if (reg) void reg.update();
    });
  };

  // 能動的なユーザ操作で即時チェック (iPad のタップ・入力も拾う)。
  // 高頻度の mousemove/scroll/touchmove は CPU/リクエスト節約のため登録しない。
  document.addEventListener("pointerdown", triggerUpdate, { passive: true });
  document.addEventListener("keydown", triggerUpdate, { passive: true });

  // タブ復帰・フォーカス・ネット復帰の各契機で更新チェック
  document.addEventListener("visibilitychange", triggerUpdate);
  window.addEventListener("focus", triggerUpdate);
  window.addEventListener("online", triggerUpdate);

  // 表示中タブの定期ポーリング (操作の無い開きっぱなしタブの安全網)
  setInterval(triggerUpdate, POLL_INTERVAL_MS);
}
